import { Dialog, DialogPanel } from "@tremor/react";
import { AlertTriangle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDeleteSite } from "../queries/sites.js";

/**
 * Confirmation dialog for deleting a site. Deleting a site is destructive:
 * it removes the site container plus every child monitor and all of their
 * history, so we spell that out explicitly rather than relying on a native
 * window.confirm(). Shared by Sites.jsx (card grid) and SiteDetail.jsx.
 *
 * Props:
 *   site       - { id, name, monitor_count }
 *   onClose    - called to dismiss the dialog
 *   onDeleted  - called with the site id after a successful delete
 */
export default function DeleteSiteDialog({ site, onClose, onDeleted }) {
  const { t } = useTranslation("sites");
  const mutation = useDeleteSite();
  const deleting = mutation.isPending;
  const count = site.monitor_count ?? 0;

  async function handleConfirm() {
    try {
      await mutation.mutateAsync(site.id);
      toast.success(t("delete.success"));
      onDeleted?.(site.id);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error ?? t("delete.error"));
    }
  }

  return (
    <Dialog open onClose={deleting ? () => {} : onClose} static>
      <DialogPanel className="max-w-md rounded-xl bg-white p-0 shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-50">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                {t("delete.title")}
              </h2>
              <p className="text-xs text-gray-500">{t("delete.irreversible")}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <p className="text-sm text-gray-700">
            {t("delete.body", { name: site.name, count })}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            {t("delete.cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={deleting}
            className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40"
          >
            {deleting ? t("delete.deleting") : t("delete.confirm")}
          </button>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
